import i18n from "i18next";
import { initReactI18next } from "react-i18next";

const CACHE_KEY = "i18n_cache_";
const CACHE_DURATION = 1000 * 30; // 30 seconds cache for API response

interface CacheItem {
  data: any;
  timestamp: number;
  language: string;
}

const i18nCache = {
  get: (language: string, namespace: string): any | null => {
    if (typeof window === "undefined") return null;

    try {
      const item = false;
      if (!item) return null;

      const cached: CacheItem = JSON.parse(item);
      const now = new Date().getTime();

      if (now - cached.timestamp > CACHE_DURATION) {
        localStorage.removeItem(`${CACHE_KEY}${language}_${namespace}`);
        return null;
      }

      return cached.data;
    } catch (error) {
      console.error("i18n cache read error:", error);
      return null;
    }
  },

  set: (language: string, namespace: string, data: any): void => {
    if (typeof window === "undefined") return;

    try {
      localStorage.setItem(
        `${CACHE_KEY}${language}_${namespace}`,
        JSON.stringify({})
      );
    } catch (error) {
      console.error("i18n cache write error:", error);
    }
  },
};

function getLanguageFromURL(path: string): string | null {
  if (!path) return null;
  console.log("i18n config path", path);

  // Check first level path (/fr or /en)
  if (path === "/fr" || path === "/en") {
    return path.substring(1);
  }

  // Split path into segments and remove empty strings
  const pathSegments = path.split("/").filter((segment) => segment);
  console.log("i18n config segments", pathSegments);

  // Iterate through segments from end to start
  for (let i = pathSegments.length - 1; i >= 0; i--) {
    const segment = pathSegments[i];
    console.log("i18n config checking segment", segment);

    if (segment.endsWith("-fr")) return "fr";
    if (segment.endsWith("-en")) return "en";
    if (segment === "fr") return "fr";
    if (segment === "en") return "en";
  }

  return null;
}

function getDefaultLanguage(path?: string): string {
  // First try to get language from URL
  if (path) {
    const urlLang = getLanguageFromURL(path);
    if (urlLang) return urlLang;
  }

  // Then try browser language
  if (typeof window !== "undefined") {
    const browserLang = window.navigator.language.substring(0, 2);
    return browserLang === "fr" ? "fr" : "fr";
  }

  // Default fallback
  return "fr";
}

// Custom backend with default values
const customBackend: any = {
  type: "backend",
  init: (services: any, backendOptions: any, i18nextOptions: any) => {
    // Initialize with empty translations
    services.resourceStore.data = {
      en: {},
      fr: {},
    };
  },
  read: async (language: string, namespace: string, callback: any) => {
    try {
      // Check cache first
      if (typeof window !== "undefined") {
        const cachedData = i18nCache.get(language, namespace);
        if (cachedData) {
          callback(null, cachedData);
          return;
        }
      }

      // Fetch from API
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND}/translation/${language}`
      );
      const data = await response.json();
      console.log('data', data);

      // Cache the response
      if (typeof window !== "undefined") {
        i18nCache.set(language, namespace, data);
      }

      // Update translations
      callback(null, data);
    } catch (error) {
      console.error("Translation fetch error:", error);
      callback(error, null);
    }
  },
};

// Export the initialization function instead of the promise
export const initI18n = async (path?: string) => {
  await i18n
    .use(initReactI18next)
    .use(customBackend)
    .init({
      lng: getDefaultLanguage(path),
      fallbackLng: false, // Disable fallback language
      backend: {
        loadPath: `${process.env.REACT_APP_BACKEND}/translation/{{lng}}`,
      },
      react: {
        useSuspense: true, // Enable suspense
      },
      interpolation: {
        escapeValue: false,
      },
      detection: {
        order: ["path", "localStorage", "navigator"],
        caches: ["localStorage"],
      },
    });

  return i18n;
};

// Add language change listener
if (typeof window !== "undefined") {
  i18n.on("languageChanged", (lng) => {
    localStorage.setItem("i18nextLng", lng);
    document.documentElement.lang = lng;
  });
}

export default i18n;
